"use client";

import { useCallback, useMemo } from "react";
import { KeyedMutator } from "swr";
import useSWRInfinite from "swr/infinite";

import SWR_KEYS from "@/constants/swr-keys";
import { IChat } from "@/interfaces/chat.interfaces";
import conversationService from "@/services/conversation.service";
import {
  AIAgentsFilter,
  ConversationFilter,
  ConversationView,
  MyConversationsFilter,
} from "@/types/conversation-filters.types";
import { ConversationsParams } from "@/types/message.types";
import { PaginatedResponse, RequestsBaseReturn } from "@/types/requests.types";

export const CONVERSATIONS_PAGE_SIZE = 30;

export type UseGetChatReturn = RequestsBaseReturn<IChat[] | null> & {
  mutate: KeyedMutator<PaginatedResponse<IChat>[]>;
  /** Carrega a página seguinte da aba/filtro atual. */
  loadMore: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  totalCount: number;
};

export interface UseGetAllConversationProps extends Partial<ConversationsParams> {
  onSuccess?: (data: IChat[]) => void;
  hasWhatsappPlatform: boolean;
  /** Quando false, não faz requisição (ex.: usuário deslogado). Evita requests 401 após logoff. */
  enabled?: boolean;
  view?: ConversationView;
  filter?: ConversationFilter;
}

type UseGetChatSwrKey = [string, number, ConversationView, ConversationFilter, number];

const useGetAllConversations = ({
  platformId,
  onSuccess,
  hasWhatsappPlatform,
  enabled = true,
  view = ConversationView.MY_CONVERSATIONS,
  filter = MyConversationsFilter.UNREAD,
}: UseGetAllConversationProps): UseGetChatReturn => {
  const shouldFetch = enabled && !!platformId && hasWhatsappPlatform;
  const normalizedFilter =
    view === ConversationView.AI_AGENTS &&
    filter !== AIAgentsFilter.ATTENDING &&
    filter !== AIAgentsFilter.CLOSED
      ? AIAgentsFilter.ATTENDING
      : filter;

  const getKey = (pageIndex: number, previousPage: PaginatedResponse<IChat> | null) => {
    if (!shouldFetch) return null;
    // Só encerra quando a página anterior veio vazia (o backend pode devolver
    // menos itens que o pageSize solicitado e ainda ter mais páginas).
    if (previousPage && previousPage.items.length === 0) return null;
    if (
      previousPage &&
      previousPage.totalCount > 0 &&
      pageIndex * CONVERSATIONS_PAGE_SIZE >= previousPage.totalCount
    ) {
      return null;
    }

    return [
      SWR_KEYS.getAllChats,
      platformId!,
      view,
      normalizedFilter,
      pageIndex + 1,
    ] as UseGetChatSwrKey;
  };


  const {
    data: pages,
    isValidating,
    error,
    mutate,
    size,
    setSize,
  } = useSWRInfinite<PaginatedResponse<IChat>, Error>(
    getKey,
    ([, _platformId, _view, _filter, pageNumber]: UseGetChatSwrKey) =>
      conversationService.getFilteredConversations({
        platformId: _platformId,
        view: _view,
        filter: _filter,
        pageNumber,
        pageSize: CONVERSATIONS_PAGE_SIZE,
      }),
    {
      revalidateOnFocus: true,
      revalidateFirstPage: true,
      onSuccess: (loadedPages) => {
        const items = loadedPages?.flatMap((page) => page.items) ?? [];
        if (onSuccess && items.length > 0) {
          onSuccess(items);
        }
      },
      onError: (swrError) => {
        // Handle error gracefully - don't block the UI
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn("Failed to load conversations:", swrError);
        }
      },
    }
  );

  const data = useMemo(
    () => (pages ? pages.flatMap((page) => page.items) : null),
    [pages]
  );
  const lastPage = pages?.[pages.length - 1];
  const totalCount = lastPage?.totalCount ?? 0;
  const loadedCount = data?.length ?? 0;
  // Prioriza o total informado pela API; se ausente, usa a heurística de página cheia.
  const hasMore = totalCount > 0 ? loadedCount < totalCount : !!lastPage && lastPage.items.length >= CONVERSATIONS_PAGE_SIZE;
  const isLoadingMore = isValidating && !!pages && size > pages.length;
  const isInitialLoading = isValidating && !pages;

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    setSize((current) => current + 1);
  }, [hasMore, isLoadingMore, setSize]);

  return {
    data,
    isLoading: isInitialLoading,
    error,
    mutate: mutate as unknown as KeyedMutator<PaginatedResponse<IChat>[]>,
    loadMore,
    hasMore,
    isLoadingMore,
    totalCount,
  };

};

export default useGetAllConversations;
